import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { z } from "zod";
import { useAuth } from "../useAuth";
import { ChiefAPIError } from "@/backend/types/backendTypes";
import axiosInstance from "@lib/axios";

const UnlinkAdAccountSchema = z.object({
  source: z.enum([
    "facebook",
    "google",
    "klaviyo",
    "pinterest",
    "tiktok",
    "outbrain",
    "msads",
    "taboola",
  ]),
  accountId: z.string(),
});

export type UnlinkAdAccountInput = z.infer<typeof UnlinkAdAccountSchema>;

const onUnlinkAdAccount = async (input: UnlinkAdAccountInput) => {
  try {
    UnlinkAdAccountSchema.parse(input);
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/account/${input.source}/${input.accountId}/unlink`,
      {}
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const apiError = axiosError.response?.data as ChiefAPIError;
      const firstErr = apiError.detail?.[0];
      throw new Error(firstErr?.msg ?? axiosError.message);
    }
    throw new Error("Unknown error occurred!");
  }
};

export const useUnlinkAdAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UnlinkAdAccountInput) => onUnlinkAdAccount(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedAdAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["adAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["adAccountsByOrganisation"] });
    },
  });
};
